/**
 * "From another creative" (Aziz's team, 2026-09-28): pick raws, cuts or finals from the
 * clinic's other creatives and copy them onto this one, into the same tab. Only the same
 * clinic, and only creatives you can see. The copy is this creative's own: removing it
 * here leaves the original alone.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Film, Search, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import { copyAssets, fetchOtherAssets } from '@/lib/queries';
import { formatBytes } from '@/lib/upload-center';
import { cn } from '@/lib/cn';

const KINDS: Array<{ v: '' | 'raw' | 'edit' | 'final'; label: string }> = [
  { v: '', label: 'All' }, { v: 'raw', label: 'Raw' }, { v: 'edit', label: 'Cuts' }, { v: 'final', label: 'Final' },
];

export default function OtherCreativePicker({ creativeId, onClose }: { creativeId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | 'raw' | 'edit' | 'final'>('');
  const rows = useQuery({ queryKey: ['other-assets', creativeId], queryFn: () => fetchOtherAssets(creativeId) });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const shown = useMemo(() => (rows.data ?? []).filter((r) => (!kind || r.asset.kind === kind) && (!q
    || r.asset.filename.toLowerCase().includes(q.toLowerCase()) || r.creative_code.toLowerCase().includes(q.toLowerCase())
    || r.creative_title.toLowerCase().includes(q.toLowerCase()))), [rows.data, kind, q]);
  // one group per source creative, newest first
  const groups = useMemo(() => {
    const m = new Map<string, { code: string; title: string; items: typeof shown }>();
    for (const r of shown) {
      const g = m.get(r.creative_id) ?? { code: r.creative_code, title: r.creative_title, items: [] };
      g.items.push(r); m.set(r.creative_id, g);
    }
    return [...m.values()];
  }, [shown]);
  const copy = useMutation({
    mutationFn: () => copyAssets(creativeId, [...picked]),
    onSuccess: (made) => {
      toast.success(made.length ? `${made.length} file${made.length === 1 ? '' : 's'} copied to this creative` : 'Those files are already on this creative');
      qc.invalidateQueries({ queryKey: ['assets', creativeId] });
      onClose();
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not copy the files'),
  });
  const toggle = (id: string) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" data-testid="other-creative-picker">
      <div className="w-full sm:max-w-4xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl bg-bg-card border border-slate-800 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="text-sm font-medium text-muted-strong inline-flex items-center gap-2"><Copy size={14} /> From another creative</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-800 text-muted" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="px-4 py-2 flex flex-wrap items-center gap-2 text-xs border-b border-slate-800">
          <div className="relative flex-1 min-w-[160px]">
            <Search size={12} className="absolute left-2.5 top-2.5 text-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Creative code, title or file name" data-testid="other-search"
              className="w-full h-8 pl-7 pr-2 rounded-lg bg-bg border border-slate-800 text-xs text-muted-strong" />
          </div>
          {KINDS.map((k) => (
            <button key={k.v} onClick={() => setKind(k.v)} className={cn('px-2.5 py-1.5 rounded-full border', kind === k.v ? 'border-accent-400 text-accent-400' : 'border-slate-800 text-muted')}>{k.label}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {rows.isLoading && <div className="text-sm text-muted">Loading the clinic's other creatives...</div>}
          {rows.isSuccess && groups.length === 0 && <div className="text-sm text-muted">No files on the clinic's other creatives match.</div>}
          {groups.map((g) => (
            <section key={g.code} data-testid="other-group">
              <div className="text-xs text-muted mb-2"><span className="font-mono text-muted-strong">{g.code}</span> · <span dir="auto">{g.title}</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {g.items.map(({ asset: a }) => {
                  const on = picked.has(a.id);
                  return (
                    <button key={a.id} onClick={() => toggle(a.id)} data-testid="other-item" aria-pressed={on}
                      className={cn('relative rounded-lg overflow-hidden border aspect-square text-left', on ? 'border-accent-400 ring-2 ring-accent-400/40' : 'border-slate-800')}>
                      {a.thumbnail_url ? <img src={a.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-muted/40"><Film size={26} /></div>}
                      <span className={cn('absolute top-1.5 left-1.5 z-10 rounded p-1 ring-1', on ? 'bg-accent-500 text-white ring-white/40' : 'bg-slate-950/70 text-white/70 ring-white/20')}><Check size={12} /></span>
                      <span className="absolute top-1.5 right-1.5 z-10 text-[9px] px-1.5 py-0.5 rounded-full bg-slate-950/80 text-white/80 uppercase">{a.kind === 'edit' ? 'cut' : a.kind}</span>
                      <span className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/95 to-transparent p-2">
                        <span className="block text-[10px] text-white/90 truncate" dir="auto">{a.filename}</span>
                        <span className="block text-[9px] text-white/60 truncate">{a.size_bytes ? formatBytes(a.size_bytes) : ''}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-slate-800 flex items-center gap-3">
          <span className="text-xs text-muted">{picked.size} picked · each goes to the same tab here</span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={picked.size === 0} loading={copy.isPending} onClick={() => copy.mutate()} data-testid="other-copy">
              Copy {picked.size || ''} to this creative
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
